/**
 * SectorEmptyBanner — affiché en haut du dashboard si l'utilisateur n'a
 * pas encore choisi ses activités. Disparaît automatiquement dès qu'au
 * moins un secteur est coché.
 *
 * UX premium : carte hero avec gradient signature, avatar Inea, CTA
 * vers /profil. Animation d'apparition douce (motion).
 *
 * Loading : ne render RIEN tant que les données ne sont pas chargées
 * (évite le flash "banner → fade out" pour les users qui ont déjà coché).
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Briefcase, ArrowRight, Sparkles } from 'lucide-react';
import { useUserSectors } from '@/hooks/useSectors';

export function SectorEmptyBanner() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, isLoading } = useUserSectors();

  // Don't flash the banner on first paint — wait for the API answer.
  if (isLoading) return null;
  // User a déjà coché au moins un secteur → ne montre rien.
  if ((data?.sectors?.length ?? 0) > 0) return null;

  return (
    <motion.button
      onClick={() => navigate('/profil')}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="group relative mb-5 flex w-full items-center gap-4 overflow-hidden rounded-[24px] border border-violet-300/50 bg-white p-5 text-left shadow-[0_12px_30px_-18px_rgba(168,85,247,0.4)] transition hover:shadow-[0_18px_40px_-18px_rgba(236,72,153,0.45)]"
    >
      {/* Background blobs */}
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full opacity-50 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(236,72,153,0.35), transparent 70%)',
        }}
      />
      <div
        className="pointer-events-none absolute -bottom-20 -left-12 h-44 w-44 rounded-full opacity-40 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(59,130,246,0.3), transparent 70%)',
        }}
      />

      <span
        className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white"
        style={{
          background:
            'linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)',
          boxShadow: '0 8px 20px -8px rgba(168,85,247,0.6)',
        }}
      >
        <Briefcase size={20} />
      </span>

      <div className="relative flex-1 min-w-0">
        <p className="font-mono text-[10.5px] font-700 uppercase tracking-[0.18em] text-pink">
          {t('dashboard.sectorEmpty.kicker', 'Pour aller plus loin')}
        </p>
        <p className="mt-1 font-display text-[16px] font-800 leading-tight tracking-[-0.015em] text-ink">
          {t('dashboard.sectorEmpty.title', "Inea ne connaît pas encore vos activités")}
        </p>
        <p className="mt-1 text-[12.5px] leading-[1.45] text-ink/60">
          {t(
            'dashboard.sectorEmpty.subtitle',
            "Cochez votre ou vos métiers pour qu'elle parle votre vocabulaire (MLM, immo, coaching, beauté…).",
          )}
        </p>
      </div>

      <span className="relative flex shrink-0 items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-[12px] font-700 text-white transition group-hover:gap-2.5">
        <Sparkles size={12} />
        {t('dashboard.sectorEmpty.cta', 'Choisir mes activités')}
        <ArrowRight size={14} />
      </span>
    </motion.button>
  );
}
